import { Fragment, useEffect } from 'react';
import { RecordListPayload, RecordListType, useDataContext } from 'context/DataStoreContext';
import { useApi } from 'model/Api';
import { generalStyle } from 'helper/Style';
import Pager from 'component/part/Pager';

const style = {
  pager: (position: 1 | 2) =>
    position === 1
      ? { marginBottom: '1rem' }
      : { marginTop: '1rem' },
  indexRow: {
    ...generalStyle.listRow,
    ['&:nth-of-type(1) > div']: {
      display: 'flex',
      flexDirection: 'row' as const,
      justifyContent: 'flex-start',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '1.2rem',
      fontWeight: '700',
      ['& > span']: {
        display: 'inline-block',
      },
    },
  },
  sorter: (order?: number, defaultOrder?: number) => ({
    cursor: 'pointer',
    ['&::after']: {
      content: `"${(order ?? defaultOrder) === 1 ? '▼' : '▲'}"`,
      color: order !== undefined ? '#000000' : '#cccccc',
    },
  }),
};

export type ListProps<T extends RecordListType> = Readonly<{
  type: T;
  indexes: { label: string | string[], key?: string }[];
  conditions?: RecordListPayload['conditions'];
  displayPager?: boolean;
  children: React.JSX.Element | React.JSX.Element[];
  className?: string;
}>;

const List = <T extends RecordListType>(props: ListProps<T>) => {
  const { state } = useDataContext();
  const api = useApi();
  const recordsState = state?.[props.type];
  const {
    page = 1,
    limit,
    perPage = 1,
    sorts = [],
  } = recordsState || {};
  const conditions = { ...recordsState?.conditions || {}, ...props.conditions || {} };
  const indexes = props.indexes.map((indexParam) => {
    const sortParam = sorts.find(({ key }) => key === indexParam.key);
    const order = sortParam?.order;
    const defaultOrder = indexParam.key === 'id' ? 1 : 0;
    const label = Array.isArray(indexParam.label)
      ? indexParam.label.map((labelText) => <span>{labelText}</span>)
      : indexParam.label;

    return { ...indexParam, label, order, defaultOrder };
  });

  const onChangePage = (page: number) => {
    api[props.type].paginate({ ...conditions, limit, sorts, page });
  };

  const onChangeSort = (sortKey: string) => {
    const sortParamIndex = sorts.findIndex(({ key }) => key === sortKey);
    if (sortParamIndex >= 0) {
      const [sortParam] = sorts.splice(sortParamIndex, 1);
      sortParam.order = 1 - sortParam.order;
      sorts.unshift(sortParam);
    } else {
      sorts.unshift({ key: sortKey, order: 0 });
    }
    api[props.type].paginate({ ...conditions, limit, sorts, page });
  };

  useEffect(() => {
    api[props.type].paginate({ ...conditions, limit, sorts, page });
  }, [props.conditions]);

  return <Fragment>
    {props.displayPager !== false && (
      <Pager onChange={(v) => onChangePage(v)} page={page} perPage={perPage} css={style.pager(1)} />
    )}
    <div css={generalStyle.list(props.indexes.length)} className={props.className}>
      <div css={style.indexRow}>
        {indexes.map((indexParam, index) => (
          <div key={index}>
            <div>{indexParam.label}</div>
            {indexParam.key && (
              <div
                onClick={() => onChangeSort(indexParam.key as string)}
                css={style.sorter(indexParam.order, indexParam.defaultOrder)}
              ></div>
            )}
          </div>
        ))}
      </div>
      {props.children}
    </div>
    {props.displayPager !== false && (
      <Pager onChange={(v) => onChangePage(v)} page={page} perPage={perPage} css={style.pager(2)} />
    )}
  </Fragment>;
};

export default List;
